
import Vue from 'vue'
import firebase from 'firebase/compat/app'
import 'firebase/compat/firestore'
import { showSnackbar } from '@/globalActions'

const DEFAULTS = {
  pre_match: 15,
  intermission: 15,
  post_match: 10,
  'penalty-shootout': 20
}

export default Vue.extend({
  name: 'MatchSettingsPage',
  data: () => ({
    loading: true,
    saving: false,
    form: { ...DEFAULTS } as Record<string, number>
  }),
  created (): void {
    this.loadSettings()
  },
  methods: {
    positiveMinutes (value: unknown, fallback: number): number {
      const n = Number(value)
      if (Number.isFinite(n) && n > 0) {
        return Math.floor(n)
      }
      return fallback
    },
    async loadSettings (): Promise<void> {
      this.loading = true
      try {
        const snap = await firebase.firestore()
          .collection('settings')
          .doc('match')
          .get()
        const data = snap.exists ? snap.data() || {} : {}
        const nested =
          data.expectedPhaseMinutes &&
          typeof data.expectedPhaseMinutes === 'object'
            ? data.expectedPhaseMinutes
            : {}
        this.form = {
          pre_match: this.positiveMinutes(
            nested.pre_match ?? data.preMatchMinutes,
            DEFAULTS.pre_match
          ),
          intermission: this.positiveMinutes(
            nested.intermission ?? data.intermissionMinutes,
            DEFAULTS.intermission
          ),
          post_match: this.positiveMinutes(
            nested.post_match ?? data.postMatchMinutes,
            DEFAULTS.post_match
          ),
          'penalty-shootout': this.positiveMinutes(
            nested['penalty-shootout'] ?? data.penaltyShootoutMinutes,
            DEFAULTS['penalty-shootout']
          )
        }
      } catch (err) {
        console.error('loadMatchSettings', err)
        showSnackbar(
          (err && (err as Error).message) || (this.$t('match-settings-load-failed') as string),
          { duration: 5000, color: 'danger' }
        )
      } finally {
        this.loading = false
      }
    },
    async save (): Promise<void> {
      const expectedPhaseMinutes = {
        pre_match: this.positiveMinutes(this.form.pre_match, DEFAULTS.pre_match),
        intermission: this.positiveMinutes(this.form.intermission, DEFAULTS.intermission),
        post_match: this.positiveMinutes(this.form.post_match, DEFAULTS.post_match),
        'penalty-shootout': this.positiveMinutes(
          this.form['penalty-shootout'],
          DEFAULTS['penalty-shootout']
        )
      }
      this.form = { ...expectedPhaseMinutes }
      this.saving = true
      try {
        await firebase.firestore()
          .collection('settings')
          .doc('match')
          .set({ expectedPhaseMinutes }, { merge: true })
        showSnackbar(this.$t('match-settings-saved') as string, { color: 'success' })
      } catch (err) {
        console.error('saveMatchSettings', err)
        showSnackbar(
          (err && (err as Error).message) || (this.$t('match-settings-save-failed') as string),
          { duration: 5000, color: 'danger' }
        )
      } finally {
        this.saving = false
      }
    }
  }
})
